<template>
  <layout
    title="Driver’s license and digital ID verification | Youverse"
    :transparent="true"
    class="overflow-x-hidden"
  >
    <highlightSection
      backgroundImage="/storage/images/industries/mobility/driver-id-verication.jpg"
      altImage="Real-time identity verification"
      title="Real-time identity verification for Mobility Service Providers"
      subtitle="Onboard customers instantly, ensure robust driver’s verification, and gain your customers trust."
      leftButton="See how it works"
      urlLeftButton="https://6992654.fs1.hubspotusercontent-na1.net/hubfs/6992654/01.%20ONE%20PAGERS/Youverse_Mobility.pdf"
    ></highlightSection>

    <EndToEnd
      :endToEndData="endToEnd"
      title="Real-time ID verification, because trust can’t wait"
    ></EndToEnd>

    <FeatureHighlightSection
      :features="features"
      title="Seamless and secure journeys anywhere, anytime"
    />
    <div class="pt-20">
      <cta
        title="Get started"
        text="Are you still taking the long route for drivers and customers authentication?"
        buttonText="Book a Demo"
        buttonLink="/book-a-meeting"
        target="_blank"
      />
    </div>

    <div
      class="mt-20 flex md:flex-row flex-col items-center justify-center w-full mx-auto px-6 sm:px-7 md:px-4 lg:px-10 xl:px-16 max-w-screen-sm md:max-w-screen-xl gap-16"
    >
      <div
        class="flex justify-center flex-col max-w-md md:text-left text-center lg:max-w-screen-xl"
      >
        <h3 class="text-3xl font-bold">
          Quickly onboard trusted drivers and customers
        </h3>
        <p class="mt-6">
          From robust driver’s license verification to seamless onboarding, we
          make your business thrive in the competitive mobility services market.
        </p>
      </div>
      <rowCardsSection
        :elements="elements"
        :hoverAnimation="false"
        class="max-w-md"
      />
    </div>

    <partners />

    <latestPosts
      title="Read our latest articles and resources"
      :tags="['mobility']"
    />

    <div class="mt-20">
      <cta
        title="Talk to our experts"
        text="Your competitors are already ahead. Are you ready?"
        buttonText="Contact us"
        buttonLink="/company#contactus"
      />
    </div>
  </layout>
</template>

<script>
const layout = () =>
  import(/* webpackChunkName: "layout" */ "../../Components/Layout/layout.vue");

const highlightSection = () =>
  import(
    /* webpackChunkName: "highlightSection" */ "../../Components/Layout/highlightSection.vue"
  );

const EndToEnd = () =>
  import(
    /* webpackChunkName: "ProductsInfoCards" */ "../../Components/Layout/UseCases/EndToEndSection.vue"
  );

const FeatureHighlightSection = () =>
  import(
    /* webpackChunkName: "FeatureHighlightSection" */ "../../Components/Layout/UseCases/FeatureHighlightSection.vue"
  );

const cta = () =>
  import(/* webpackChunkName: "cta" */ "../../Components/Layout/cta.vue");

const rowCardsSection = () =>
  import(
    /* webpackChunkName: "rowCardsSection" */ "../../Components/Layout/rowCardsSection.vue"
  );

const partners = () =>
  import(
    /* webpackChunkName: "partners" */ "../../Components/Layout/HomePage/partners.vue"
  );

const latestPosts = () =>
  import(
    /* webpackChunkName: "latestPosts" */ "../../Components/Layout/latestPosts.vue"
  );

export default {
  props: {
    errors: Object,
  },
  components: {
    layout,
    highlightSection,
    EndToEnd,
    FeatureHighlightSection,
    cta,
    rowCardsSection,
    partners,
    latestPosts,
  },
  data() {
    return {
      endToEnd: [
        {
          title: "Onboarding",
          iconPath: "/storage/images/icons/onboarding.svg",
          description: ["Driver registration", "Customer registration"],
        },
        {
          title: "Identity Verification",
          iconPath: "/storage/images/icons/mobility_identity_verification.svg",
          description: ["Driver’s license verification", "KYC/AML"],
        },
        {
          title: "Continuous Authentication",
          iconPath: "/storage/images/icons/user-shield.svg",
          description: ["Automated re-verification"],
        },
        {
          title: "Fraud Detection",
          iconPath: "/storage/images/icons/fraud.svg",
          description: ["Automated ID fraud detection"],
        },
      ],
      elements: [
        {
          title: `Identity Verification`,
          text: `With YouID, you can integrate identity verification flows into your business processes in just minutes.`,
          seeMoreUrl: `/products/youID`,
          imageSrc: `/storage/images/home/icons/identity_verification.svg`,
          imageAlt: `Identity Verification`,
          imageStyle: `w-14 h-14`,
        },
      ],
      features: [
        {
          title: "Onboard customers instantly",
          description:
            "Deliver smooth and quick onboarding, even in low-light conditions and in low-network areas, for hassle-free user journeys.",
          image: "/storage/images/industries/mobility/drivers_license_uber.png",
        },
        {
          title: "Ensure robust driver verification",
          description:
            "Protect passengers and vehicles with real-time identity verification, ensuring only trusted users access your platform. ",
          image:
            "/storage/images/industries/mobility/drivers_license_rider.png",
        },
        {
          title: "Gain your customers trust",
          description:
            "Streamline the driver's license verification process with speed, accuracy, and security to enhance customer trust.",
          image: "/storage/images/industries/mobility/riders_verification.png",
        },
      ],
    };
  },
};
</script>
